import { ZTooltip } from '../ztooltip';
import React, { FunctionComponent, Suspense } from 'react';
import { graphql, useFragment } from 'react-relay';
import CreditsAlert from './creditsAlert';
import { useNavigate } from 'react-router-dom';
import { backofficeUrl, T } from 'src/shared/helpers';
import AngleDown from 'src/shared/svg/angleDown';
import BellSolid from 'src/shared/svg/bellSolid';
import GlobeEuropeSolid from 'src/shared/svg/globeEuropeSolid';
import StoreAltSolid from 'src/shared/svg/storeAltSolid';
import UserAltSolid from 'src/shared/svg/userAltSolid';
import { useAppState } from '../../../shared/appState';
import QuickSearch from '../quickSearch/quickSearch';
import { rightMenuFragment$key } from './__generated__/rightMenuFragment.graphql';
import { ContainerNotification, ContainerText, ImgIcon, ImgIconUser, MenuItem, MenuItemIcon, MenuRight, NoWrap, SeparatorMenu } from './globalsToolbar';
import HeadwayWidget from './headwayWidget';
import LanguagePopupMenu from './languagePopupMenu';
import UserPopupMenu from './userPopupMenu';
import Zendesk from './widgetZenDeskHelp';

interface RightMenuProps {
    isFakeMenu: boolean;
    isMenuHidden: boolean;
    userInfo: any;
    ecommerceSettings: any;
    closeAll: () => void;
    setIsUserPopupMenuVisible: (visible: boolean) => void;
    isUserPopupMenuVisible: boolean;
    setIsLanguagePopupMenuVisible: (visible: boolean) => void;
    isLanguagePopupMenuVisible: boolean;
    isVisitorMode: boolean;
    goToYourStore: (remoteUrl: string) => void;
    handleLoaded: () => void;
}

const RightMenu: FunctionComponent<RightMenuProps> = ({
    isFakeMenu,
    isMenuHidden,
    userInfo,
    ecommerceSettings,
    closeAll,
    setIsUserPopupMenuVisible,
    isUserPopupMenuVisible,
    setIsLanguagePopupMenuVisible,
    isLanguagePopupMenuVisible,
    isVisitorMode,
    goToYourStore,
    handleLoaded
}) => {
    const navigate = useNavigate();
    const { queryData } = useAppState();
    const query = useFragment<rightMenuFragment$key>(
        graphql`
            fragment rightMenuFragment on Query {
                ecommerceSettings {
                    definitiveUrl
                }
                subscription {
                    planVersion
                }
            }
        `,
        isFakeMenu ? null : queryData!
    );

    return (
        <MenuRight $visitorMode={!userInfo?.userID || userInfo.userID < 0 || isVisitorMode}>
            {!isMenuHidden && userInfo?.userID && userInfo.userID > 0 && userInfo.name && (
                <React.Fragment>
                    <QuickSearch />
                    <SeparatorMenu />

                    <ImgIcon
                        onClick={() =>
                            ecommerceSettings?.ecommerceName &&
                            ecommerceSettings.ecommerceName.toUpperCase() !== 'NONE' &&
                            goToYourStore(query?.ecommerceSettings?.definitiveUrl ?? '')
                        }
                        className={!ecommerceSettings?.ecommerceName || ecommerceSettings.ecommerceName.toUpperCase() === 'NONE' ? 'grayed' : ''}
                    >
                        {!ecommerceSettings?.ecommerceName || ecommerceSettings.ecommerceName.toUpperCase() === 'NONE' ? (
                            <ZTooltip html={<label>{T._('Once you connect your store you can click this icon to access your storefront', 'Admin')}</label>}>
                                <StoreAltSolid />
                            </ZTooltip>
                        ) : (
                            <StoreAltSolid />
                        )}
                    </ImgIcon>

                    <SeparatorMenu />

                    <MenuItem onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <ImgIcon
                            onClick={() => {
                                closeAll();
                                setIsLanguagePopupMenuVisible(!isLanguagePopupMenuVisible);
                            }}
                        >
                            <GlobeEuropeSolid />
                        </ImgIcon>
                        <LanguagePopupMenu isPopupVisible={isLanguagePopupMenuVisible} />
                    </MenuItem>

                    <SeparatorMenu />

                    <ImgIcon>
                        <BellSolid />
                        <ContainerNotification>
                            {/* Headway Widget can only be rendered one time */}
                            {!isFakeMenu && <HeadwayWidget />}
                        </ContainerNotification>
                    </ImgIcon>

                    <SeparatorMenu />
                </React.Fragment>
            )}

            {userInfo?.userID && userInfo.userID > 0 && userInfo.name && (
                <React.Fragment>
                    <MenuItem onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <ImgIconUser
                            onClick={() => {
                                closeAll();
                                setIsUserPopupMenuVisible(!isUserPopupMenuVisible);
                            }}
                        >
                            <UserAltSolid />
                        </ImgIconUser>
                    </MenuItem>
                    <MenuItem onClick={(e) => e.nativeEvent.stopImmediatePropagation()}>
                        <NoWrap
                            $padding='0 5px'
                            onClick={() => {
                                if (!isVisitorMode) {
                                    closeAll();
                                    setIsUserPopupMenuVisible(!isUserPopupMenuVisible);
                                } else {
                                    window.location.href = backofficeUrl('/Login');
                                }
                            }}
                        >
                            <ContainerText data-track='your account'> {T._('Your Account', 'Admin')} </ContainerText>
                        </NoWrap>
                        <MenuItemIcon
                            onClick={() => {
                                closeAll();
                                setIsUserPopupMenuVisible(!isUserPopupMenuVisible);
                            }}
                        >
                            {!isVisitorMode && <AngleDown />}
                        </MenuItemIcon>
                        {isUserPopupMenuVisible && userInfo.name && (
                            <UserPopupMenu
                                nameUser={userInfo.name}
                                isMenuHidden={isMenuHidden}
                                ecommerceName={ecommerceSettings?.ecommerceName ?? ''}
                                userId={userInfo.userID}
                                planVersion={query?.subscription?.planVersion ?? 'VERSION_2'}
                                onItemClick={() => setIsUserPopupMenuVisible(false)}
                            />
                        )}
                    </MenuItem>
                    {!isVisitorMode && <SeparatorMenu />}
                </React.Fragment>
            )}

            {(!userInfo?.userID || userInfo.userID < 0) && (
                <React.Fragment>
                    <MenuItem>
                        <ImgIconUser
                            onClick={() => {
                                closeAll();
                                setIsUserPopupMenuVisible(true);
                            }}
                        >
                            <UserAltSolid />
                        </ImgIconUser>
                    </MenuItem>
                    <MenuItem onClick={() => navigate(backofficeUrl('/Login'))}>
                        <NoWrap
                            $padding='0 5px'
                            onClick={() => {
                                closeAll();
                                setIsUserPopupMenuVisible(true);
                            }}
                        >
                            <ContainerText data-track='your account'> {T._('Your Account', 'Admin')} </ContainerText>
                        </NoWrap>
                    </MenuItem>
                </React.Fragment>
            )}
            {!isFakeMenu && !isMenuHidden && <Zendesk defer onLoaded={handleLoaded} userName={userInfo?.name ?? ''} userEmail={userInfo?.email ?? ''} />}
            {!isFakeMenu && (
                <Suspense fallback={null}>
                    <CreditsAlert />
                </Suspense>
            )}
        </MenuRight>
    );
};

export default RightMenu;
